import React, { useState } from 'react';
import { colors } from '../styles/colors';

interface Props {
  isOpen: boolean;
  recruiterName: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const DeleteRecruiterModal: React.FC<Props> = ({
  isOpen,
  recruiterName,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason);
    }
  };

  const handleCancel = () => {
    setReason('');
    onCancel();
  };

  return (
    <div style={styles.overlay} onClick={handleCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Excluir Recrutador</h2>
          <button onClick={handleCancel} style={styles.closeBtn} disabled={loading}>×</button>
        </div>
        
        <div style={styles.body}>
          <p style={styles.warning}>
            ⚠️ Você está prestes a excluir o recrutador: <strong>{recruiterName}</strong>
          </p>
          <p style={styles.info}>
            Esta ação irá:
          </p>
          <ul style={styles.list}>
            <li>Desativar todos os relacionamentos com empresas</li>
            <li>Substituir o e-mail por um identificador único</li>
            <li>Marcar o recrutador como excluído</li>
          </ul>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Motivo da exclusão (obrigatório):</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={styles.textarea}
              placeholder="Digite o motivo da exclusão..."
              rows={4}
              disabled={loading}
              required
            />
          </div>
        </div>

        <div style={styles.footer}>
          <button 
            onClick={handleCancel} 
            style={styles.cancelBtn}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            onClick={handleConfirm} 
            style={{
              ...styles.deleteBtn,
              ...((!reason.trim() || loading) ? styles.disabledBtn : {})
            }}
            disabled={!reason.trim() || loading}
          >
            {loading ? 'Excluindo...' : 'Excluir Recrutador'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    maxWidth: '600px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: colors.error,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  body: {
    padding: '1.5rem',
  },
  warning: {
    margin: '0 0 1rem 0',
    padding: '1rem',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    color: '#721c24',
  },
  info: {
    margin: '1rem 0 0.5rem 0',
    fontWeight: 'bold',
    color: colors.text,
  },
  list: {
    margin: '0 0 1.5rem 1.5rem',
    color: colors.textLight,
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
  },
  footer: {
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  deleteBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default DeleteRecruiterModal;
